/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  MouseEventHandler,
  useCallback,
  useImperativeHandle
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormRadioGroup} from '@ion/dls/src/components/forms/FormRadioGroup';
import styles from '@ion/dls/src/editors/editorStyles';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface SwitchVehicleProfileDetailsFormProps {
  handleActionChooser?: MouseEventHandler;
  onSave: (details: GenericObject) => void;
};

const useStyles = (theme: DLSTheme) => ({
  message : {
    fontSize   : '16px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    lineHeight : '24px',
    marginTop  : '15px'
  }
});

export enum VehicleProfileFields {
  Profile = 'profile',
  TestProfile = 'Test Profile',
  ProductionProfile = 'Production Profile'
};

const ACTION_OPTIONS = [{
  label    : VehicleProfileFields.TestProfile,
  value    : VehicleProfileFields.TestProfile,
  disabled : false
},
{
  label    : VehicleProfileFields.ProductionProfile,
  value    : VehicleProfileFields.ProductionProfile,
  disabled : false
}];

export const SwitchVehicleProfileDetailsForm = forwardRef(({onSave}: SwitchVehicleProfileDetailsFormProps, ref) => {
  const editorStyles = useExtendStyles(styles, useStyles);
  const classes      = editorStyles();
  const [formState, setFormState] = useFormState({
    [VehicleProfileFields.TestProfile] : {
      name     : VehicleProfileFields.TestProfile,
      value    : true,
      required : false
    }
  });

  const getStatusAction = useCallback((action) => {
    return isEqual(action, VehicleProfileFields.TestProfile);
  }, []);

  const handleFormState = useCallback((action): void => {
    setFormState({
      [VehicleProfileFields.TestProfile] : {
        name  : VehicleProfileFields.TestProfile,
        value : getStatusAction(action)
      }
    });
  }, []);

  const onSubmit = useFormSave({
    formState,
    serialize,
    setFormState,
    onSave
  });

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  return (
    <>
      <EmbeddedForm>
        <FormItem fullWidth>
          <div className={classes.root}>
            <FormRadioGroup required
              displayAsRow
              name={VehicleProfileFields.Profile}
              options={ACTION_OPTIONS}
              defaultValue={formState[VehicleProfileFields.TestProfile]?.value && VehicleProfileFields.TestProfile}
              groupLabel={translate('common:editors.bulkActions.vehicleProfile.switchDevice')}
              onChange={handleFormState}/>
          </div>
        </FormItem>
        <div className={classes.message}>
          {translate('common:editors.bulkActions.vehicleProfile.note')}
        </div>
      </EmbeddedForm>
    </>
  );
});

const serialize = (formState: FormState) => ({
  testProfile : formState[VehicleProfileFields.TestProfile]?.value
});

export default memo(SwitchVehicleProfileDetailsForm);
